export default function (context) {
  context.$axios.onRequest((config) => {
    config.withCredentials = true;
    config.headers.common['X-PLANNR-ACCOUNT-UUID'] = context.store.getters['user/currentAccountUuid'];

    // Send the Echo socket id so the backend can exclude this client from its own
    // broadcasts (broadcast()->toOthers()). Only present once Echo has connected.
    const socketId = context.app.$echo?.socketId?.();
    if (socketId) {
      config.headers.common['X-Socket-ID'] = socketId;
    }

    return config;
  });

  // Ping the backend after every 25 API requests to keep the user's session cookie from expiring if they're active.
  let apiCounter = 0;

  context.$axios.onRequest((config) => {
    if (!config.url.startsWith($nuxt.$api.laravelURL + '/api')) {
      return;
    }

    apiCounter++;

    if (apiCounter % 25 === 0) {
      apiCounter = 0;
      context.$authService.refreshSession();
    }
  });

  context.$axios.onError(async (error) => {
    // If the user's session cookie has expired but their remember me cookie is still valid then refresh their session and retry the original request.
    if (
      error.response &&
      error.response.status === 401 &&
      context.$authService.shouldRefreshSession() &&
      error.config.url.startsWith($nuxt.$api.laravelURL + '/api') &&
      !error.config.url.startsWith($nuxt.$api.laravelURL + '/refresh')
    ) {
      const response = await context.$authService.refreshSession();

      if (response.status === 204) {
        return context.$axios.request(error.config);
      }

      return Promise.reject(error);
    }

    // Otherwise we have a real authentication error so log the user out and redirect.
    if (error.response && error.response.status === 401 && context.$authService.loggedIn) {
      await context.$authService.nuxtLogout();

      if (context.$authService.authStrategy === 'plannrProduction') {
        context.$authService.redirectToLaravel();
        return Promise.resolve();
      }

      context.redirect('/');
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 500) {
      // Callers that handle their own error UI (e.g. a background token
      // refresh with its own retry/backoff) can opt out of the global toast
      // by setting `suppressErrorNotification: true` on the request config —
      // otherwise every background retry during an outage would also toast.
      if (!error.config?.suppressErrorNotification) {
        $nuxt.$emit(
          'notification',
          'Server error. Please contact info@plannrcrm.com if the error still persists.',
          'warning',
          true,
          10000
        );
      }

      return Promise.reject(error);
    }

    // Backend middleware (plan.not-deleting) returns 409 on any write to a plan-scoped
    // resource when the plan is being deleted asynchronously. Surface the message and
    // optimistically flip is_deleting:true on the active plan so the UI stops offering
    // doomed edits.
    if (
      error.response &&
      error.response.status === 409 &&
      error.response.data?.message === 'Plan is being deleted.' &&
      error.config?.url &&
      /\/api\/v1\/plans\/[^/]+/.test(error.config.url) &&
      !['get', 'head', 'options'].includes((error.config.method || '').toLowerCase())
    ) {
      $nuxt.$emit('notification', error.response.data.message, 'warning', true);

      const match = error.config.url.match(/\/api\/v1\/plans\/([^/?#]+)/);
      const planUuid = match ? match[1] : null;
      const activePlan = context.store.state.plan?.plan;
      if (planUuid && activePlan && activePlan.uuid === planUuid && !activePlan.is_deleting) {
        context.store.commit('plan/SET_PLAN', { ...activePlan, is_deleting: true });
      }
    }

    return Promise.reject(error);
  });
}
